import { useCallback, useState } from "react";
import PropTypes from "prop-types";
import useSpeechToText from "../../../../hooks/useSpeechToText";
import { showInfoToast } from "../../toastUtils";
import {
  HandwritingPadModal,
  HandwritingPenButton,
} from "../handwriting";
import appendTranscript from "./appendTranscript";
import SpeechMicButton from "./SpeechMicButton";

/**
 * Wraps a textarea with mute/unmute dictation and optional handwriting / e-pen.
 * Use via InputAreaField `enableSpeechToText` / `enableHandwriting`
 * or SpeechToTextArea directly.
 */
const SpeechToTextWrapper = ({
  value,
  onChange,
  disabled = false,
  children,
  micSize = "md",
  showListeningHint = true,
  className = "",
  enableSpeechToText = true,
  enableHandwriting = false,
  onHandwritingImage,
}) => {
  const [padOpen, setPadOpen] = useState(false);

  const handleTranscript = useCallback(
    (chunk) => {
      onChange(appendTranscript(value, chunk));
    },
    [value, onChange]
  );

  const { isListening, isSupported, toggle } = useSpeechToText({
    onTranscript: handleTranscript,
  });

  const handleMicClick = () => {
    if (!isSupported) {
      showInfoToast("Speech recognition is not supported in this browser. Try Chrome or Edge.");
      return;
    }
    toggle();
  };

  const handleInsertHandwritingText = useCallback(
    (text) => {
      onChange(appendTranscript(value, text));
    },
    [value, onChange]
  );

  const handleInsertHandwritingImage = useCallback(
    (dataUrl) => {
      onHandwritingImage?.(dataUrl);
    },
    [onHandwritingImage]
  );

  const showMic = enableSpeechToText && isSupported;
  const showToolbar = showMic || enableHandwriting;
  const toolbarCount = (showMic ? 1 : 0) + (enableHandwriting ? 1 : 0);
  const speechPaddingClass =
    toolbarCount >= 2 ? "pr-20" : toolbarCount === 1 ? "pr-12" : "";

  return (
    <div className={`relative flex flex-col min-w-0 ${className}`}>
      {showToolbar && (
        <div className="absolute right-2 top-2 z-10 flex items-center gap-1">
          {enableHandwriting && (
            <HandwritingPenButton
              onClick={() => setPadOpen(true)}
              disabled={disabled}
              size={micSize}
            />
          )}
          {enableSpeechToText && (
            <SpeechMicButton
              isListening={isListening}
              isSupported={isSupported}
              onClick={handleMicClick}
              size={micSize}
            />
          )}
        </div>
      )}
      {typeof children === "function"
        ? children({
            speechPaddingClass,
            isListening,
            isSupported,
          })
        : children}
      {enableSpeechToText && showListeningHint && isListening && (
        <p className="mt-1 text-xs font-medium text-red-600 flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full bg-red-500 animate-pulse" />
          Listening… speak now. Click mic again to stop.
        </p>
      )}
      {enableSpeechToText && showListeningHint && !isSupported && (
        <p className="mt-0.5 text-[10px] text-gray-400">Voice input unavailable in this browser.</p>
      )}

      {enableHandwriting && (
        <HandwritingPadModal
          isOpen={padOpen}
          onClose={() => setPadOpen(false)}
          onInsertText={handleInsertHandwritingText}
          onInsertImage={handleInsertHandwritingImage}
        />
      )}
    </div>
  );
};

SpeechToTextWrapper.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]).isRequired,
  micSize: PropTypes.oneOf(["sm", "md", "lg"]),
  showListeningHint: PropTypes.bool,
  className: PropTypes.string,
  enableSpeechToText: PropTypes.bool,
  enableHandwriting: PropTypes.bool,
  onHandwritingImage: PropTypes.func,
};

export default SpeechToTextWrapper;
